<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <PageHeader :isFilter="false"
                        :title="$t('reimbursement_form_detail')"/>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :isFilter="false"
                              :title="$t('reimbursement_form_detail')"/>
            </template>

            <div class="container-fluid px-0 mb-5" v-if="purchaseOrderFormData.id">

                <!-- Action Header Row -->
                <div class="row mb-4">
                    <div class="col-12 d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center">
                            <b-badge :variant="getBadgeVariant(purchaseOrderFormData.status)" class="px-3 py-2 badge-pill text-uppercase mr-3 font-weight-bold">
                                {{ purchaseOrderFormData.status_name }}
                            </b-badge>
                        </div>
                        <div class="d-flex align-items-center">
                            <b-button
                                variant="outline-danger"
                                class="d-flex align-items-center px-4 py-2 border-2 mr-2"
                                v-if="checkPermission('reimbursementform_myrequestapprovalstepsreport')"
                                @click="downloadApprovalStepsReport"
                            >
                                <i class="ri-file-pdf-line mr-2 ri-lg"></i>
                                <span class="font-weight-bold">{{ $t('approval_steps_report').toUpperCase() }}</span>
                            </b-button>
                            <b-button variant="outline-primary" class="d-flex align-items-center px-4 py-2 border-2" @click="$router.push('/my-staff-requests')">
                                <i class="ri-arrow-left-line mr-2 ri-lg"></i>
                                <span class="font-weight-bold">{{ $t('back').toUpperCase() }}</span>
                            </b-button>
                        </div>
                    </div>
                </div>

                <!-- Metric Row -->
                <div class="row mb-4">
                    <div class="col-12 col-sm-6 col-md-4 mb-3 mb-md-0">
                        <div class="metric-card bg-gradient-green text-white p-3 rounded-lg shadow-sm">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <span class="metric-label">{{ $t('grand_total') }}</span>
                                <div class="metric-icon-box"><i class="ri-money-dollar-circle-line"></i></div>
                            </div>
                            <h2 class="metric-value mb-0">{{ currencyFormat(purchaseOrderFormData.grand_total) }}</h2>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md-4 mb-3 mb-md-0">
                        <div class="metric-card bg-gradient-purple text-white p-3 rounded-lg shadow-sm">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <span class="metric-label">{{ $t('items') }}</span>
                                <div class="metric-icon-box"><i class="ri-list-check"></i></div>
                            </div>
                            <h2 class="metric-value mb-0">{{ (purchaseOrderFormData.items && purchaseOrderFormData.items.length) || 0 }}</h2>
                        </div>
                    </div>
                </div>

                <!-- Profile and Extra Details Section -->
                <div class="row mb-4">
                    <!-- Profile Card -->
                    <div class="col-12 col-lg-4 mb-4 mb-lg-0">
                        <div class="card h-100 shadow-sm border-0 rounded-lg overflow-hidden">
                            <div class="profile-header-bg"></div>
                            <div class="card-body pt-0 text-center position-relative">
                                <div class="profile-avatar-wrapper shadow">
                                    <span class="avatar-initials">{{ getInitials(purchaseOrderFormData.full_name || purchaseOrderFormData.name) }}</span>
                                </div>
                                <h4 class="font-weight-bold text-dark mb-1 text-capitalize mt-3">
                                    {{ purchaseOrderFormData.full_name }}
                                </h4>

                                <div class="pt-3 border-top text-left mt-3">
                                    <div class="mb-3 d-flex align-items-center">
                                        <i class="ri-user-2-line text-theme mr-3 ri-xl"></i>
                                        <div>
                                            <span class="profile-meta-label">{{ $t('employee_id') }}</span>
                                            <div class="profile-meta-value">{{ purchaseOrderFormData.employee_id || '-' }}</div>
                                        </div>
                                    </div>
                                    <div class="mb-3 d-flex align-items-center">
                                        <i class="ri-shield-user-line text-theme mr-3 ri-xl"></i>
                                        <div>
                                            <span class="profile-meta-label">{{ $t('job_title') }}</span>
                                            <div class="profile-meta-value">{{ purchaseOrderFormData.job_title_name || '-' }}</div>
                                        </div>
                                    </div>
                                    <div class="mb-3 d-flex align-items-center">
                                        <i class="ri-building-line text-theme mr-3 ri-xl"></i>
                                        <div>
                                            <span class="profile-meta-label">{{ $t('budgetary_department') }}</span>
                                            <div class="profile-meta-value">{{ purchaseOrderFormData.budgetary_department_name || '-' }}</div>
                                        </div>
                                    </div>
                                    <div class="d-flex align-items-center" v-if="purchaseOrderFormData.supervisor_name || purchaseOrderFormData.supervisor_fullname">
                                        <i class="ri-user-follow-line text-theme mr-3 ri-xl"></i>
                                        <div>
                                            <span class="profile-meta-label">{{ $t('supervisor') }}</span>
                                            <div class="profile-meta-value">{{ purchaseOrderFormData.supervisor_name || purchaseOrderFormData.supervisor_fullname }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Application Info -->
                    <div class="col-12 col-lg-8">
                        <div class="card h-100 shadow-sm border-0 rounded-lg">
                            <div class="card-body p-4">
                                <div class="d-flex align-items-center mb-4 pb-3 border-bottom">
                                    <div class="icon-wrapper bg-soft-theme mr-3">
                                        <i class="ri-survey-line text-theme ri-xl"></i>
                                    </div>
                                    <h5 class="card-title mb-0 text-dark font-weight-bold">{{ $t('application_information') }}</h5>
                                </div>

                                <div class="row">
                                    <div class="col-12 mb-4">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ $t('notes') }}</span>
                                            <div class="info-value text-dark">{{ purchaseOrderFormData.requester_notes || purchaseOrderFormData.notes || '-' }}</div>
                                        </div>
                                    </div>
                                    <div class="col-sm-6 mb-4" v-if="purchaseOrderFormData.expense_category_name">
                                        <div class="p-3 bg-light rounded-lg border-left-theme">
                                            <span class="info-label">{{ $t('expense_category') }}</span>
                                            <div class="info-value text-dark">{{ purchaseOrderFormData.expense_category_name }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Items Table -->
                <div class="row mb-4" v-if="purchaseOrderFormData.items && purchaseOrderFormData.items.length">
                    <div class="col-12">
                        <div class="card shadow-sm border-0 rounded-lg overflow-hidden">
                            <div class="card-header bg-light p-4 border-0">
                                <div class="d-flex align-items-center">
                                    <div class="icon-wrapper bg-soft-theme mr-3">
                                        <i class="ri-list-check-2 ri-xl"></i>
                                    </div>
                                    <h5 class="mb-0 text-dark font-weight-bold">{{ $t('items') }}</h5>
                                </div>
                            </div>
                            <div class="card-body p-4 bg-white">
                                <b-table :fields="itemsTableFields" :items="purchaseOrderFormData.items" bordered striped responsive class="mb-0">
                                    <template #cell(index)="data">
                                        {{ data.index + 1 }}
                                    </template>
                                    <template #cell(total)="data">
                                        {{ currencyFormat(data.item.total) }}
                                    </template>
                                </b-table>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Modern Workflow Timeline Section -->
                <div class="row mb-4" v-if="steps && steps.length">
                    <div class="col-12">
                        <div class="card shadow-sm border-0 rounded-lg overflow-hidden">
                            <div class="card-header bg-light p-4 border-0">
                                <div class="d-flex align-items-center">
                                    <div class="icon-wrapper bg-soft-dark mr-3">
                                        <i class="ri-git-commit-line text-dark ri-xl"></i>
                                    </div>
                                    <h5 class="mb-0 text-dark font-weight-bold">
                                        {{ $t('approval_procedures') }}
                                    </h5>
                                </div>
                            </div>
                            <div class="card-body p-4 bg-white">
                                <ApprovalTimeline
                                    :steps="steps"
                                    :selected-active-step="selectedActiveStep"
                                    :allow-edit="allowEdit"
                                    :is-loading="isLoading"
                                    permission="approvalstep_updatestatus"
                                    :status.sync="updateForm.status"
                                    :reason.sync="updateForm.reason"
                                    :statuses="statuses"
                                    @save="stepUpdate"
                                >
                                    <!-- Step 1 Form Actions: Supervisor Approval (is_budgeted) -->
                                    <template v-slot:step-1>
                                        <div v-if="selectedActiveStep">
                                            <ValidationObserver ref="supervisorForm">
                                                <b-row class="mb-3">
                                                    <b-col cols="12" md="6">
                                                        <ValidationProvider name="is_budgeted" rules="required" v-slot="{ errors }">
                                                            <b-form-group :label="$t('is_budgeted')">
                                                                <YesNoSelectbox
                                                                    v-model="step2Data.is_budgeted"
                                                                    :isBoolean="true"
                                                                    :validate-error="errors[0]"
                                                                />
                                                            </b-form-group>
                                                        </ValidationProvider>
                                                    </b-col>
                                                </b-row>
                                            </ValidationObserver>
                                        </div>
                                    </template>
                                </ApprovalTimeline>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Files Card (At the bottom of the page) -->
                <div class="row" v-if="(purchaseOrderFormData.evidence_files && purchaseOrderFormData.evidence_files.length) || (purchaseOrderFormData.files && purchaseOrderFormData.files.length)">
                    <div class="col-12">
                        <div class="card shadow-sm border-0 rounded-lg overflow-hidden">
                            <div class="card-header bg-light p-4 border-0">
                                <div class="d-flex align-items-center">
                                    <div class="icon-wrapper bg-soft-theme mr-3">
                                        <i class="ri-attachment-line ri-xl"></i>
                                    </div>
                                    <h5 class="mb-0 text-dark font-weight-bold">{{ $t('files') }}</h5>
                                </div>
                            </div>
                            <div class="card-body p-4 bg-white">
                                <div class="row">
                                    <div
                                        v-for="file in (purchaseOrderFormData.evidence_files || purchaseOrderFormData.files)"
                                        :key="file.uuid"
                                        class="col-12 col-md-6 mb-2"
                                    >
                                        <div class="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
                                            <div class="d-flex align-items-center overflow-hidden">
                                                <i class="ri-file-line mr-2 text-primary ri-lg"></i>
                                                <span class="small font-weight-bold text-truncate" style="max-width: 350px;">{{ file.file_name || file.uuid }}</span>
                                            </div>
                                            <b-button
                                                variant="link"
                                                size="sm"
                                                class="text-primary p-0 ml-2"
                                                v-if="checkPermission('reimbursementform_myrequestdownload')"
                                                @click="downloadFile(file)"
                                            >
                                                <i class="ri-download-line ri-lg"></i>
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </app-layout>
    </div>
</template>

<script>
// Template
import AppLayout from "@/layouts/AppLayout";
import PageHeader from "@/layouts/AppLayout/Header";
import HeaderMobile from "@/layouts/AppLayout/HeaderMobile";

// Services
import ReimbursementFormService from "@/services/ReimbursementFormService";
import ApprovalStepService from "@/services/ApprovalStepService";

// Components
import { ValidationObserver, ValidationProvider } from "vee-validate";
import YesNoSelectbox from "@/components/interactive-fields/YesNoSelectbox.vue";
import ApprovalTimeline from "@/components/elements/ApprovalTimeline.vue";

export default {
    name: "StaffReimbursementDetail",
    components: {
        AppLayout,
        PageHeader,
        HeaderMobile,
        ValidationObserver,
        ValidationProvider,
        YesNoSelectbox,
        ApprovalTimeline
    },
    metaInfo() {
        return {
            title: this.$t('reimbursement_form_detail')
        }
    },
    data() {
        return {
            id: this.$route.params.id,
            purchaseOrderFormData: {},
            steps: [],
            statuses: {},
            updateForm: {
                id: null,
                status: null,
                reason: null,
            },
            step2Data: {
                is_budgeted: null
            },
            isLoading: false,
        }
    },
    created() {
        this.getData();
    },
    computed: {
        userRoles() {
            return this.$store.getters['auth/getActiveRole']
        },
        allowEdit() {
            return !['approved', 'declined'].includes(this.purchaseOrderFormData.status);
        },
        selectedActiveStep() {
            if (this.purchaseOrderFormData.active_step) {
                const activeStep = this.purchaseOrderFormData.active_step;
                if (!activeStep.approvers || activeStep.approvers.length === 0) {
                    return false;
                }
                const user = this.$store.getters["auth/getUser"];
                return activeStep.approvers.some(approver =>
                    approver.name === this.userRoles ||
                    approver.id === user.id ||
                    approver.user_id === user.id ||
                    (approver.name === user.name && approver.surname === user.surname) ||
                    (user.person && approver.name === user.person.name && approver.surname === user.person.surname)
                );
            }
            return false;
        },
        itemsTableFields() {
            return [
                { key: 'index', label: '#' },
                { key: 'type_of_expense', label: this.toUpperCase('type_of_expense') },
                { key: 'expense_category', label: this.toUpperCase('expense_category') },
                { key: 'payment_method_name', label: this.toUpperCase('payment_method') },
                { key: 'description', label: this.toUpperCase('description') },
                { key: 'total', label: this.toUpperCase('total'), thClass: 'text-right', tdClass: 'text-right' }
            ];
        },
    },
    methods: {
        getData() {
            ReimbursementFormService.getMyRequest(this.id)
                .then((response) => {
                    this.purchaseOrderFormData = response.data.data;
                    this.steps = (this.purchaseOrderFormData.steps ?? []).sort((a, b) => a.rank - b.rank);
                    if (this.purchaseOrderFormData.active_step != null) {
                        this.updateForm.id = this.purchaseOrderFormData.active_step.id;
                        this.statuses = this.purchaseOrderFormData.active_step.statuses ?? {};
                    }

                    if (this.purchaseOrderFormData.is_budgeted !== null) {
                        this.step2Data.is_budgeted = !!this.purchaseOrderFormData.is_budgeted;
                    } else {
                        this.step2Data.is_budgeted = null;
                    }
                })
                .catch((error) => {
                    this.showErrors(error);
                });
        },
        downloadApprovalStepsReport() {
            ReimbursementFormService.getMyRequestApprovalStepsReport(this.id)
                .then((response) => {
                    this._downloadFile(response, `approval-steps-report-${this.id}.pdf`);
                })
                .catch((error) => {
                    this.showErrors(error);
                });
        },


        stepUpdate() {
            const activeStep = this.purchaseOrderFormData.active_step;
            const isApproved = this.updateForm.status === 'approved';

            const runStepUpdate = () => {
                this.$swal.fire({
                    title: this.$t('warning'),
                    text: this.$t('are_you_sure_to_start_process'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no'),
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.isLoading = true;

                        let payload = { status: this.updateForm.status };
                        if (this.updateForm.status === 'declined') {
                            payload.reason = this.updateForm.reason;
                        } else if (isApproved && activeStep && activeStep.rank === 1) {
                            payload.is_budgeted = (this.step2Data.is_budgeted === true || this.step2Data.is_budgeted === 1) ? 1 : 0;
                        }

                        ApprovalStepService.update(this.updateForm.id, payload)
                            .then(response => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                      this.resetActionForms();
                                this.getData();
                            })
                            .catch(error => {
                                this.showErrors(error, this.$refs.supervisorForm);
                            })
                            .finally(() => {
                                this.isLoading = false;
                            });
                    }
                });
            };

            if (isApproved && activeStep && activeStep.rank === 1) {
                const supervisorForm = Array.isArray(this.$refs.supervisorForm) ? this.$refs.supervisorForm[0] : this.$refs.supervisorForm;
                if (!supervisorForm) return;
                supervisorForm.validate().then(valid => {
                    if (!valid) {
                        this.$toast.error(this.$t('you_must_fill_in_the_fields_indicated_in_red'));
                        return;
                    }
                    runStepUpdate();
                });
            } else {
                runStepUpdate();
            }
        },

        resetActionForms() {
            this.updateForm.status = null;
            this.updateForm.reason = null;
            this.step2Data.is_budgeted = null;
        },
        downloadFile(file) {
            ReimbursementFormService.downloadMyRequestFile(file.uuid)
                .then(response => {
                    this._downloadFile(response, file.file_name);
                })
                .catch(error => {
                    if (error.status == 422) {
                        this.$toast.error(error.data.errors.file[0]);
                    }
                    if (error.status == 406) {
                        this.$toast.error(this.$t('api.' + error.data.message));
                    }
                });
        },
        getInitials(name) {
            if (!name) return 'U';
            const parts = name.split(' ');
            return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
        },
        getBadgeVariant(status) {
            switch(status) {
                case 'approved':
                case 'completed':
                    return 'success';
                case 'declined':
                case 'rejected':
                    return 'danger';
                case 'pending':
                case 'waiting_requester_review':
                case 'waiting_cfo_or_president_review':
                case 'waiting_po_review':
                case 'waiting_po_approval':
                    return 'warning';
                default:
                    return 'secondary';
            }
        }
    }
}
</script>

<style scoped>
.font-weight-bold {
    font-weight: 600 !important;
}
.icon-wrapper {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.bg-soft-dark {
    background-color: rgba(30, 41, 59, 0.1);
}
.bg-soft-theme {
    background-color: rgba(14, 177, 249, 0.1);
}
.text-theme {
    color: rgb(14, 177, 249) !important;
}

.metric-card {
    border-radius: 12px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    color: #ffffff !important;
}
.metric-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(14, 177, 249, 0.15);
}
.metric-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    opacity: 0.9;
    color: #ffffff !important;
}
.metric-value {
    font-size: 2rem;
    font-weight: 800;
    color: #ffffff !important;
}
.metric-icon-box {
    width: 32px;
    height: 32px;
    background-color: rgba(255,255,255,0.25);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
}
.bg-gradient-green {
    background: linear-gradient(135deg, rgb(14, 177, 249) 0%, #0ca7c1 100%);
}
.bg-gradient-purple {
    background: linear-gradient(135deg, rgb(14, 177, 249) 0%, #066d9c 100%);
}
.profile-header-bg {
    height: 90px;
    background: linear-gradient(90deg, rgb(14, 177, 249) 0%, #008ece 100%);
}
.profile-avatar-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: white;
    margin: -40px auto 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid white;
}
.avatar-initials {
    font-size: 1.8rem;
    font-weight: 800;
    color: rgb(14, 177, 249);
}
.profile-meta-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: #8c9ba5;
    text-transform: uppercase;
}
.profile-meta-value {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2c3e50;
}
.border-left-theme {
    border-left: 3px solid rgb(14, 177, 249);
}
.info-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #8c9ba5;
    letter-spacing: 0.5px;
    display: block;
    margin-bottom: 2px;
}
.info-value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2c3e50;
}
.border-2 {
    border-width: 2px !important;
}
</style>
